import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Tooltip, Alert } from 'antd';
import { useAppStore } from '../../stores';
import { ComparisonActionProvider } from '../../context/ComparisonActionProvider';
import { ContractComparisonRenderer } from '..';
import './CompareContractModal.less';

const CompareContractModal: React.FC = () => {
    const { contractComparisonStore } = useAppStore();

    const [isVisible, setIsVisible] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);

    const selectedComparison = contractComparisonStore.selectedContractComparison;

    const getClassName = () => {
        let className = 'compare-contract-modal';
        const matching = selectedComparison?.sectionMatching;

        if (matching && !matching.isConfirmed) {
            className += ' matching-only';
        }

        return className;
    };

    const handleClose = () => {
        if (selectedComparison?.isSaving) {
            return;
        }

        setIsVisible(false);
    };

    const handleSave = async () => {
        if (!selectedComparison || selectedComparison.isSaving) {
            return;
        }

        setSaveError(null);
        try {
            if (await selectedComparison.saveChanges()) {
                handleClose();
            }
        } catch {
            setSaveError('Unable to save comparison changes. Your edits are still available. Please try again.');
        }
    };

    React.useEffect(() => {
        setIsVisible(!!selectedComparison);
        setSaveError(null);
    }, [selectedComparison]);

    return (
        <Modal
            className={getClassName()}
            title="Contract Comparison"
            visible={isVisible}
            maskClosable={false}
            closable={!selectedComparison?.isSaving}
            keyboard={!selectedComparison?.isSaving}
            width={1000}
            destroyOnClose
            forceRender
            centered
            onCancel={handleClose}
            afterClose={() => {
                contractComparisonStore.setSelectedFilter('all');
                selectedComparison?.close();
            }}
            footer={[
                <Button
                    key="cancel-button"
                    className="light alpha-dialog-button"
                    onClick={handleClose}
                    disabled={selectedComparison?.isSaving}
                >
                    Cancel
                </Button>,
                <Tooltip key="save-button" title={selectedComparison?.businessTypeConflict}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        onClick={handleSave}
                        disabled={!selectedComparison?.canSaveChanges}
                        loading={selectedComparison?.isSaving}
                    >
                        Save changes
                    </Button>
                </Tooltip>
            ]}
        >
            {saveError && <Alert type="error" message={saveError} showIcon />}
            {selectedComparison && (
                <ComparisonActionProvider contractComparison={selectedComparison}>
                    <fieldset className="comparison-draft" disabled={selectedComparison.isSaving}>
                        <ContractComparisonRenderer contractComparison={selectedComparison} />
                    </fieldset>
                </ComparisonActionProvider>
            )}
        </Modal>
    );
};

export default observer(CompareContractModal);
